import { submitFormJson, getContentByUrl } from 'widgets/toolbox/ajax';
import { appendParamsToUrl } from 'widgets/toolbox/util';
import { scrollWindowTo } from 'widgets/toolbox/scroll';

import { TInputCheckboxInstance } from 'widgets/forms/InputCheckbox';
import { TShippingFormInstance } from 'widgets/checkout/ShippingForm';
import { TBillingFormInstance } from 'widgets/checkout/BillingForm';
import { TProcessButtonInstance } from 'widgets/global/ProcessButton';
import { TModalInstance } from 'widgets/global/Modal';
import { TOrderModel } from 'types/models/order';

export type TSummarySubmissionResult = TServerResponse & {
    continueUrl: string;
    orderID: string;
    orderToken: string;
    errorStage?: {
        stage: 'shipping' | 'billing' | 'payment';
    };
};
export type TSummarySubmissionError = TResponseError & {
    order?: TOrderModel;
    errorStage: {
        stage: 'shipping' | 'billing' | 'payment';
    };
};

/**
 * @description Checkout Summary step logic
 * @param CheckoutMgr Base widget for extending
 */
function SummaryStepMixinClassCreator(CheckoutMgr: import('widgets/checkout/CheckoutMgr').TCheckoutMgr) {
    /**
     * @category widgets
     * @subcategory checkout
     * @class SummaryStepMixin
     * @augments CheckoutMgr
     * @classdesc Represents summary step component with the next features:
     * 1. Manage Summary step state on the checkout
     * 2. Activate/Deactivate Summary step
     * 3. Submit Summary step
     */
    class SummaryStepMixin extends CheckoutMgr {
        /**
         * @description Activate summary step
         * @emits "checkout.summary.step.activated"
         */
        summaryActivate() {
            if (this.order?.steps?.billing?.hasErrorFields) {
                this.showBillingAddressCorrectionModal();
            }

            this.eventBus().emit('checkout.summary.step.activated', this);
        }

        /**
         * @description Show modal with billing address to allow customer correct not valid fields
         */
        showBillingAddressCorrectionModal() {
            const correctionFormUrl = this.order?.steps?.billing?.correctionFormUrl;
            const correctionAddressModal = this.getById('billingAddressCorrectingModal', (addressModal: TModalInstance) => {
                return addressModal;
            });

            if (correctionAddressModal) {
                correctionAddressModal.showModal();
            } else {
                getContentByUrl(correctionFormUrl)
                    .then((response) => {
                        this.ref('self').append(response);
                    });
            }
        }

        /**
         * @description Deactivate summary step
         */
        summaryDeActivate() {
            this.ref('summaryError').hide();
        }

        /**
         * @description Returns agreeToSubscription checkbox
         * @returns agreeToSubscription checkbox
         */
        getSubscriptionCheckbox(): TInputCheckboxInstance {
            let subscriptionCheckbox;

            this.getByIdDeep('agreeToSubscription', (agreeToSubscription: TInputCheckboxInstance) => {
                subscriptionCheckbox = agreeToSubscription;
            });

            return subscriptionCheckbox;
        }

        /**
         * @description Submit summary form
         * @param additionalData - additional data with "Place Order" button for disabling/enabling
         */
        summarySubmit(additionalData: Array<TProcessButtonInstance>) {
            if (this.order?.steps?.billing?.hasErrorFields) {
                this.showBillingAddressCorrectionModal();

                return;
            }

            if (!this.validatePlaceOrderForm() || this.prefs().processingRequest) {
                return;
            }

            let agreeToSubscription = 'false';

            const submitBtn = additionalData.find(el => {
                return el?.id === 'submitButton';
            }) as TProcessButtonInstance;

            this.ref('summaryError').hide();
            this.setPref('processingRequest', true);
            submitBtn?.startProcess();

            const subscriptionCheckbox = this.getSubscriptionCheckbox();

            if (subscriptionCheckbox) {
                agreeToSubscription = subscriptionCheckbox.getValue() || 'false';
            }

            submitFormJson(
                this.prefs().urlPlaceOrder,
                { agreeToSubscription }
            ).then((response) => {
                this.handleSummarySubmissionResponse(response, submitBtn);
            }).catch(() => {
                this.setPref('processingRequest', false);
                submitBtn?.stopProcess();
            });
        }

        /**
         * @description Check summary step validity
         * @returns Validation result
         */
        validatePlaceOrderForm() {
            return true;
        }

        /**
         * @description Handle summary submission response
         * @param response - Server response object
         * @param submitBtn - submit button for enabling
         */
        handleSummarySubmissionResponse(response: TSummarySubmissionResult, submitBtn: TProcessButtonInstance) {
            if (response && response.redirectUrl) {
                window.location.assign(response.redirectUrl);
            } else if (response && !response.success) {
                this.handleSummarySubmissionError(response as (TSummarySubmissionError), submitBtn);
            } else if (response && response.continueUrl) {
                let continueUrl = response.continueUrl;

                this.summarySubmitted(response);

                if (!response.omitOrderParamsInURL) {
                    continueUrl = appendParamsToUrl(response.continueUrl, {
                        ID: response.orderID,
                        token: response.orderToken
                    });
                }

                window.location.assign(continueUrl);
            }
        }

        /**
         * @description Handle summary submission error
         * @param response - Server response object
         * @param submitBtn - submit button for enabling
         */
        handleSummarySubmissionError(response: TSummarySubmissionError, submitBtn: TProcessButtonInstance) {
            this.setPref('processingRequest', false);
            submitBtn?.stopProcess();

            if (response.errorStage) {
                if (['billing', 'payment'].includes(response.errorStage.stage)) {
                    this.activateStepByName('billing');

                    if (response.order) {
                        this.eventBus().emit('order.updated', response.order);
                    }

                    this.getById(this.prefs().billingFormID, (billingFormComponent: TBillingFormInstance) => {
                        billingFormComponent.setError(response.errorMessage);
                    });
                } else if (response.errorStage.stage === 'shipping') {
                    this.activateStepByName('shipping');
                    this.getById(this.prefs().shippingFormID, (shippingFormComponent: TShippingFormInstance) => {
                        shippingFormComponent.setError(response.errorMessage);
                    });
                }
            } else if (response.errorMessage) {
                const summary = this.ref('summaryError');

                summary.setText(response.errorMessage).show();

                const summaryElement = summary.get();

                if (summaryElement) {
                    scrollWindowTo(summaryElement, true);
                }
            }
        }

        /**
         * @description Summary submission result handler
         * @param response - Server response object
         */
        summarySubmitted(response) {
            const subscriptionCheckbox = this.getSubscriptionCheckbox();
            const customerEmail = response?.analytics?.customerEmail;

            if (subscriptionCheckbox && subscriptionCheckbox.getValue() && customerEmail) {
                /**
                 * @description Global event after newsletter subscription
                 * @event "newsletter.confirmed"
                 */
                this.eventBus().emit('newsletter.confirmed', customerEmail);
            }
        }
    }

    return SummaryStepMixin;
}

export type TSummaryStepMixin = ReturnType<typeof SummaryStepMixinClassCreator>;

export type TSummaryStepMixinInstance = InstanceType<TSummaryStepMixin>;

export default SummaryStepMixinClassCreator;
